// IndexedDB persistence for in-flight upload file bytes.
// localStorage can't hold file data, so the upload queue metadata lives in
// Zustand persist while the bytes live here — together they let an
// interrupted upload resume after the tab is closed and reopened.
//
// Bytes are stored as ArrayBuffer (+ mime type), not Blob: some WebKit builds
// reject Blob/File values in object stores ("Error preparing Blob/File data").

const DB_NAME = 'maxflow-media-uploads'
const STORE_NAME = 'files'
const DB_VERSION = 1

interface StoredFile {
  buffer: ArrayBuffer
  type: string
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME)
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  const db = await openDb()
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, mode)
      const request = fn(tx.objectStore(STORE_NAME))
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  } finally {
    db.close()
  }
}

export async function saveUploadBlob(uploadId: string, blob: Blob): Promise<void> {
  const stored: StoredFile = { buffer: await blob.arrayBuffer(), type: blob.type }
  await withStore('readwrite', (store) => store.put(stored, uploadId))
}

export async function getUploadBlob(uploadId: string): Promise<Blob | undefined> {
  const stored = await withStore<StoredFile | undefined>('readonly', (store) => store.get(uploadId))
  if (!stored) return undefined
  return new Blob([stored.buffer], { type: stored.type })
}

export async function deleteUploadBlob(uploadId: string): Promise<void> {
  await withStore('readwrite', (store) => store.delete(uploadId))
}
